import {type ReactNode} from "react";
import {Head, usePage} from "@inertiajs/react";
import classNames from "classnames";
import {Effects} from "./Effects";
import {GlobalModals} from "../overlays/GlobalModals";
import type {CountryOption} from "@/entities/country";
import type {User} from "@/entities/user";
import {useScrollDirection} from "@/features/header";
import {Button} from "@/shared/ui/Button";
import {Svg} from "@/shared/ui/Svg";
import {Header} from "@/widgets/header";
import {Footer, type FooterData} from "@/widgets/footer";
import {MobileNavBar} from "@/widgets/mobile-nav-bar";
import {Cookie} from "@/widgets/cookie";
import styles from "./baseLayout.module.scss";
import ModalWindows from "@/Components/Site/ModalWindows"; // Legacy

type LayoutPageProps = {
    title: string;
    description: string;
    pathCanonical: string;
    robots: string;
    user: User;
    hideHeader: boolean;
    countryList: CountryOption[];
    imgVersion: string;
    catalogMenu: string[];
    hideFooter: boolean;
    hideMainFooter: boolean;
    footerData: FooterData;
    hideMobileNavBar: boolean;
    hideCookie: boolean;
    promoCodeFirstOrder: string;
};

type BaseLayoutProps = {
    children: ReactNode;
};

export const BaseLayout = ({children}: BaseLayoutProps) => {
    const {props} = usePage<LayoutPageProps>();
    const {
        title = '',
        description = '',
        pathCanonical = '',
        robots = '',
        hideMobileNavBar,
        user,
        hideHeader,
        countryList,
        imgVersion,
        catalogMenu,
        hideFooter,
        hideMainFooter,
        footerData,
        hideCookie,
        promoCodeFirstOrder,
    } = props;
    const {scrollY} = useScrollDirection();

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name={`description`} content={description} />
                <link rel={`canonical`} href={pathCanonical} />
                <meta name={`robots`} content={robots} />
            </Head>
            <div id={`site`} className={styles.siteContainer}>
                <Effects />
                <Header
                    hideHeader={hideHeader}
                    user={user}
                    countryList={countryList}
                    imgVersion={imgVersion}
                    catalogMenu={catalogMenu}
                />
                <main className={styles.main}>
                    {children}
                </main>
                <Footer
                    hideFooter={hideFooter}
                    hideMainFooter={hideMainFooter}
                    footerData={footerData}
                />
                <MobileNavBar
                    hideMobileNavBar={hideMobileNavBar}
                    user={user}
                />
            </div>
            <Button
                className={classNames(styles.backToTop, {[styles.visible]: scrollY > 112})}
                onClick={() => window.scrollTo(0, 0)}
            >
                <Svg id={`arrowUp`} className={styles.arrowBackToTop} />
            </Button>
            <Cookie hideCookie={hideCookie} />
            <GlobalModals
                user={user}
                promoCodeFirstOrder={promoCodeFirstOrder}
            />
            <ModalWindows />
        </>
    );
}
